import { Link } from "@tanstack/react-router";
import { SERVICES } from "@/content/services";
import { Rise, Section, SectionHeader } from "./primitives";

/**
 * Core services. Titles and one-line summaries are pulled from the
 * centralized service dataset so the homepage can never drift from the
 * service pages it links to.
 */
const PRIORITY: { slug: string; label?: string; blurb: string }[] = [
  { slug: "technical-seo", blurb: "Crawl, indexation, rendering, Core Web Vitals and schema — fixed, not just documented." },
  { slug: "local-seo", blurb: "Map Pack visibility, service-area pages and review velocity for businesses that serve a radius." },
  { slug: "content-strategy", label: "Content SEO", blurb: "Commercial-intent pages and topical clusters written to close a real gap." },
  { slug: "aeo-geo", label: "AI Search Optimization", blurb: "Entity clarity, structured data and answer-shaped content for assistants and AI Overviews." },
  { slug: "link-building", blurb: "Editorial placements earned with assets the business genuinely owns." },
  { slug: "digital-pr", blurb: "Press and industry coverage that builds brand signals alongside links." },
  { slug: "google-business-profile", blurb: "Categories, services, photos and posting cadence treated as a product surface." },
  { slug: "ecommerce-seo", blurb: "Category architecture, product schema and faceted navigation done properly." },
  { slug: "migration-seo", blurb: "Redesigns and replatforms planned so hard-won search equity survives launch." },
  { slug: "analytics-reporting", label: "Measurement", blurb: "Search Console, GA4 and dashboards reconciled into one honest read." },
];

export function HomeServicesV2() {
  const known = new Map(SERVICES.map((s) => [s.slug, s]));
  const items = PRIORITY.filter((p) => known.has(p.slug)).map((p) => ({
    ...p,
    title: p.label ?? known.get(p.slug)!.title,
  }));

  return (
    <Section labelledBy="services-heading">
      <div className="container-page py-20 md:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeader
            id="services-heading"
            eyebrow="What we do"
            title={<>The disciplines that move modern search.</>}
            intro="Each one is a full service in its own right. Most engagements run several together, sequenced against what is actually holding the site back."
          />
          <Link
            to="/services/"
            className="text-sm text-primary underline-offset-4 hover:underline"
          >
            All services →
          </Link>
        </div>

        <ul className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
          {items.map((s, i) => (
            <Rise as="li" key={s.slug} delay={(i % 4) as 0 | 1 | 2 | 3}>
              <Link
                to="/services/$slug/"
                params={{ slug: s.slug }}
                className="group flex h-full flex-col justify-between gap-6 bg-background p-6 transition-colors duration-300 hover:bg-surface md:p-7"
              >
                <div>
                  <span
                    aria-hidden
                    className="font-mono text-xs text-muted-foreground"
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-3 font-display text-lg leading-tight transition-colors group-hover:text-primary">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {s.blurb}
                  </p>
                </div>
                <span
                  aria-hidden
                  className="text-primary transition-transform duration-300 group-hover:translate-x-1"
                >
                  →
                </span>
              </Link>
            </Rise>
          ))}
        </ul>
      </div>
    </Section>
  );
}
